import React, { useEffect, useMemo, useRef } from "react";
import "./VerificationCodeInput.css";
import { elakayAutoText } from "../../../includes/pageInside/dataElakay";


const DEFAULT_LENGTH = 6;

const sanitizeDigits = (value = "") => String(value || "").replace(/\D/g, "");

// Shared 6-box verification-code input, used by every page that asks for a
// 6-digit code (account activation, new-device signin confirmation, and any
// future one). Digits only, one character per box, auto-advance/backspace/
// arrow-key navigation, full-string paste, autofill support
// (autoComplete="one-time-code" on the first box so iOS/Android SMS
// autofill and password managers can fill all 6 boxes at once), and
// auto-submits via onComplete as soon as the last digit is entered --
// callers are responsible for guarding against double-submission (see
// active.js/Signin.js's lastSubmittedCodeRef usage).
const VerificationCodeInput = ({
    value = "",
    onChange,
    onComplete,
    disabled = false,
    hasError = false,
    autoFocus = false,
    ariaLabel,
    length = DEFAULT_LENGTH,
}) => {
    const inputRefs = useRef([]);
    const digits = useMemo(() => sanitizeDigits(value).slice(0, length).split(""), [value, length]);

    useEffect(() => {
        if (autoFocus) {
            inputRefs.current[0]?.focus();
        }
        // Only on mount -- re-focusing on every value change would steal
        // focus away from wherever the user currently is.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const emitChange = (nextDigits) => {
        const nextValue = nextDigits.join("");
        onChange?.(nextValue);
        if (nextValue.length === length && /^\d+$/.test(nextValue)) {
            onComplete?.(nextValue);
        }
    };

    const focusIndex = (index) => {
        const clamped = Math.max(0, Math.min(length - 1, index));
        inputRefs.current[clamped]?.focus();
        inputRefs.current[clamped]?.select?.();
    };

    const handleChange = (index) => (event) => {
        const raw = sanitizeDigits(event.target.value);
        if (!raw) {
            // Cleared via selection+typing over an existing digit, or a
            // non-digit character got filtered out entirely.
            const next = [...digits];
            next[index] = "";
            emitChange(next);
            return;
        }

        // A single box can only ever hold one character (maxLength=1), but
        // some mobile keyboards/autofill can still deliver more than one
        // character in a single change event -- treat that the same as a
        // paste, distributing from this box onward.
        if (raw.length > 1) {
            const next = [...digits];
            let cursor = index;
            for (const char of raw) {
                if (cursor >= length) break;
                next[cursor] = char;
                cursor += 1;
            }
            emitChange(next);
            focusIndex(Math.min(cursor, length - 1));
            return;
        }

        const next = [...digits];
        next[index] = raw;
        emitChange(next);
        if (index < length - 1) focusIndex(index + 1);
    };

    const handleKeyDown = (index) => (event) => {
        if (event.key === "Backspace") {
            event.preventDefault();
            const next = [...digits];
            if (next[index]) {
                // Clear the current box, stay in place.
                next[index] = "";
                emitChange(next);
            } else if (index > 0) {
                // Already empty -- clear the previous box and move back to it.
                next[index - 1] = "";
                emitChange(next);
                focusIndex(index - 1);
            }
            return;
        }

        if (event.key === "Delete") {
            event.preventDefault();
            const next = [...digits];
            next[index] = "";
            emitChange(next);
            return;
        }

        if (event.key === "ArrowLeft") {
            event.preventDefault();
            focusIndex(index - 1);
            return;
        }

        if (event.key === "ArrowRight") {
            event.preventDefault();
            focusIndex(index + 1);
        }
    };

    const handlePaste = (index) => (event) => {
        event.preventDefault();
        const pasted = sanitizeDigits(event.clipboardData?.getData("text")).slice(0, length);
        if (!pasted) return;

        const next = [...digits];
        let cursor = index;
        for (const char of pasted) {
            if (cursor >= length) break;
            next[cursor] = char;
            cursor += 1;
        }
        emitChange(next);
        focusIndex(Math.min(cursor, length - 1));
    };

    const handleFocus = (event) => {
        event.target.select?.();
    };

    return (
        <div
            className={`verification-code-input ${hasError ? "has-error" : ""}`}
            role="group"
            aria-label={ariaLabel || elakayAutoText("h_e74a5278f9b7f6", "6-digit verification code")}
        >
            {Array.from({ length }).map((_, index) => (
                <input
                    key={index}
                    ref={(element) => { inputRefs.current[index] = element; }}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    maxLength={1}
                    className={`verification-code-box ${digits[index] ? "is-filled" : ""} ${hasError ? "has-error" : ""}`}
                    value={digits[index] || ""}
                    disabled={disabled}
                    onChange={handleChange(index)}
                    onKeyDown={handleKeyDown(index)}
                    onPaste={handlePaste(index)}
                    onFocus={handleFocus}
                    aria-label={elakayAutoText("h_1a1ca3e5c7f67b", "Digit {value0} of {value1}", { value0: index + 1, value1: length })}
                />
            ))}
        </div>
    );
};

export default VerificationCodeInput;
